import React, { useMemo, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client/react';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';
import 'survey-core/survey-core.min.css';
import moment from 'moment';
import { Notify } from 'notiflix';

import { GET_COURSE_FEEDBACK_FORM } from 'helper/graphql/queries';
import { SUBMIT_COURSE_FEEDBACK } from 'helper/graphql/mutations';
import { PATH_DASHBOARD, PATH_EVENTS } from 'routes/Paths';

const CourseFeedbackPage = () => {
	const { guid } = useParams();
	const [submitted, setSubmitted] = useState(false);

	const { data, loading } = useQuery(GET_COURSE_FEEDBACK_FORM, {
		variables: { guid },
		fetchPolicy: 'cache-and-network',
	});

	const course = useMemo(
		() => data?.allCourses?.edges?.[0]?.node || null,
		[data]
	);

	const [submitFeedback, { loading: submitting }] = useMutation(SUBMIT_COURSE_FEEDBACK);

	const survey = useMemo(() => {
		if (!course?.feedbackFormSchema) return null;
		const raw = course.feedbackFormSchema;
		const schema = typeof raw === 'string' ? JSON.parse(raw) : raw;
		const model = new Model(schema);
		model.showCompletedPage = false;
		return model;
	}, [course?.feedbackFormSchema]);

	useEffect(() => {
		if (!survey) return;
		survey.onComplete.add(async (sender) => {
			try {
				await submitFeedback({
					variables: {
						courseGuid: guid,
						feedbackData: sender.data,
					},
				});
				setSubmitted(true);
			} catch (err) {
				Notify.failure(err?.message || 'Failed to submit feedback. Please try again.');
				survey.clear(false, true);
			}
		});
	}, [survey]);

	// ── Loading ──────────────────────────────────────────────────────────────
	if (loading && !data) {
		return (
			<div className="cfp-loading">
				<Spinner animation="border" size="sm" className="me-2" />
				Loading feedback form…
			</div>
		);
	}

	// ── Course not found ──────────────────────────────────────────────────────
	if (!loading && !course) {
		return (
			<Container className="py-5 text-center">
				<i className="fe fe-alert-circle cfp-error-icon"></i>
				<h4 className="mt-3">Course not found</h4>
				<Link to={PATH_DASHBOARD.courses.browse} className="btn cfp-btn mt-3">
					Browse Courses
				</Link>
			</Container>
		);
	}

	// ── No feedback form configured ───────────────────────────────────────────
	if (!loading && course && !course.feedbackFormSchema) {
		return (
			<Container className="py-5 text-center">
				<i className="fe fe-file-text cfp-info-icon"></i>
				<h4 className="mt-3">No feedback form available</h4>
				<p className="text-muted">The feedback form for this course has not been set up yet.</p>
				<Link to={PATH_DASHBOARD.courses.browse} className="btn cfp-btn mt-3">
					Browse Courses
				</Link>
			</Container>
		);
	}

	// ── Success ───────────────────────────────────────────────────────────────
	if (submitted) {
		return (
			<div className="cfp-page pt-lg-8 pb-lg-16 pt-8 pb-12">
				<Container>
					<Row className="justify-content-center">
						<Col xs={12} md={8} lg={6}>
							<div className="cfp-success">
								<div className="cfp-success-icon-wrap">
									<i className="fe fe-check cfp-success-icon"></i>
								</div>
								<h3 className="cfp-success-title">Thank you!</h3>
								<p className="cfp-success-sub">
									Your feedback has been submitted. Your e-certificate of completion will be sent to your email shortly.
								</p>
								<Link to={PATH_EVENTS.certificates} className="btn cfp-btn">
									<i className="fe fe-award me-2"></i>View My Certificates
								</Link>
							</div>
						</Col>
					</Row>
				</Container>
			</div>
		);
	}

	// ── Feedback form ─────────────────────────────────────────────────────────
	return (
		<div className="cfp-page">
			<Container>
				<Row className="justify-content-center">
					<Col xs={12} md={10} lg={8}>
						{/* Course header */}
						<div className="cfp-course-header mb-4">
							<p className="cfp-course-label mb-1">Course Feedback</p>
							<h2 className="cfp-course-title">{course?.title}</h2>
							<div className="cfp-course-meta">
								{course?.courseStartDate && (
									<span className="cfp-meta-item">
										<i className="fe fe-calendar me-1"></i>
										{moment(course.courseStartDate).format('MMM D')}
										{course?.courseEndDate && ` – ${moment(course.courseEndDate).format('MMM D, YYYY')}`}
									</span>
								)}
							</div>
						</div>

						{/* Survey */}
						<Card className="cfp-survey-card">
							<Card.Body className="p-0">
								{survey && <Survey model={survey} />}
							</Card.Body>
						</Card>

						<p className="cfp-footer-note mt-3">
							<i className="fe fe-award me-1"></i>
							Completing this form will trigger your e-certificate of completion.
						</p>
					</Col>
				</Row>
			</Container>

			<style>{`
				.cfp-loading {
					display: flex; align-items: center; justify-content: center;
					min-height: 50vh; color: #94a3b8; font-size: 0.875rem;
				}
				.cfp-error-icon, .cfp-info-icon {
					font-size: 3rem; color: #e2e8f0; display: block; margin-bottom: 0.5rem;
				}
				.cfp-page { padding: 2.5rem 0 4rem; }
				.cfp-course-header { text-align: center; }
				.cfp-course-label {
					font-size: 0.75rem; font-weight: 700; color: #1d9eb0;
					text-transform: uppercase; letter-spacing: 0.5px;
				}
				.cfp-course-title {
					font-size: 1.75rem; font-weight: 800; color: #1e293b; margin-bottom: 0.75rem;
				}
				.cfp-course-meta {
					display: flex; align-items: center; justify-content: center;
					gap: 1.25rem; flex-wrap: wrap;
				}
				.cfp-meta-item { font-size: 0.875rem; color: #64748b; display: flex; align-items: center; }
				.cfp-survey-card {
					border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden;
					box-shadow: 0 4px 16px rgba(0,0,0,0.06);
				}
				.cfp-footer-note {
					font-size: 0.8125rem; color: #94a3b8; text-align: center;
					display: flex; align-items: center; justify-content: center;
				}
				.cfp-success {
					text-align: center; padding: 3rem 2rem;
					border: 1px solid #e2e8f0; border-radius: 20px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.06);
					background: white;
				}
				.cfp-success-icon-wrap {
					width: 80px; height: 80px; border-radius: 50%; margin: 0 auto 1.5rem;
					background: linear-gradient(135deg, #10b981, #059669);
					display: flex; align-items: center; justify-content: center;
					box-shadow: 0 8px 20px rgba(16,185,129,0.3);
				}
				.cfp-success-icon { font-size: 2.25rem; color: white; }
				.cfp-success-title { font-size: 1.625rem; font-weight: 800; color: #1e293b; margin-bottom: 0.75rem; }
				.cfp-success-sub { font-size: 0.9375rem; color: #64748b; max-width: 360px; margin: 0 auto 1.5rem; }
				.cfp-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px;
					padding: 0.625rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
					display: inline-flex; align-items: center;
				}
				.cfp-btn:hover { opacity: 0.9; color: white; }
			`}</style>
		</div>
	);
};

export default CourseFeedbackPage;
